<script setup lang="ts">
import {
  IconAttachment,
  IconSend,
  useDisclosure,
  useSingleDisclosureEnforcing,
  useHotkey,
  userInjectionKey,
  guest,
  notifyScreenKeyboardHidden,
  notifyScreenKeyboardShown,
  IconSchedule,
  SlideTransition,
} from '#shared'
import { computed, inject, ref, unref, useTemplateRef, capitalize, defineAsyncComponent, watch } from 'vue'
import { usePingsAutocompleteController } from '#widgets/message-editor/lib/use-pings-autocomplete-controller'
import { useEmojiController, StaticEmojiSelect } from '#features/emoji-selection'
import type { GifObject } from '#features/gif-selection'
import { useCancelMessageEdit, useEditingMessage } from '#features/message-editing'
import { useCancelRespond, useRespondFor } from '#features/message-responds'
import { useChatId } from '#entities/chat'
import { useEditMessageMutation, type AttachmentDto, type SendMessagePayload } from '#entities/message'
import { useSendMessageMutation } from '#entities/message/state/send-message-mutation'
import { useIsMobile } from '#shared/lib/viewport/use-is-mobile'
import { useAttachmentsController } from '../../lib/use-attachments-controller'
import { useTextLimitController } from '../../lib/use-text-limit-controller'
import AttachmentsModal from '../attachments/attachments-modal.vue'
import MessageEditorActions from '../message-editor-actions.vue'
import MessageEditorBbCodeToggle from '../message-editor-bb-code-toggle.vue'
import MessageEditorEditingMessage from '../message-editor-editing-message.vue'
import MessageEditorRespond from '../message-editor-respond.vue'
import PingsAutocomplete from '../pings/pings-autocomplete.vue'
import ScheduleModal from '../schedule/schedule-modal.vue'

const GifSelect = defineAsyncComponent(() => import('#features/gif-selection').then(module => module.GifSelect))

const { variant = 'regular' } = defineProps<{ variant?: 'regular' | 'compact' }>()

const currentUser = inject(userInjectionKey)
const isMobile = useIsMobile()
const chatId = useChatId()

const contentEl = useTemplateRef('contentEl')
const inputEl = useTemplateRef('inputEl')

const text = ref('')
const isCurrentUserAdmin = computed(() => {
  const currentUserVal = unref(currentUser)
  if (currentUserVal === guest) return false
  return currentUserVal?.isAdmin ?? false
})
const isBBCode = ref(false)
const placeholder = computed(() => (isMobile.value || variant === 'compact' ? 'Message...' : 'Enter message...'))
const inputRowCount = computed(() => text.value.split('\n').length)
const textLimitController = useTextLimitController(text)
const inputHeight = computed(() => {
  const rowCap = variant === 'compact' ? 3 : 7
  const fontSize = variant === 'compact' ? 14 : 16
  const lineHeight = 1.3
  const rowCount = Math.min(inputRowCount.value, rowCap)
  return rowCount * fontSize * lineHeight
})

const attachments = ref<AttachmentDto[]>([])
const attachmentsController = useAttachmentsController(attachments)

const pingsAutocompleteController = usePingsAutocompleteController(inputEl)

const emojiController = useEmojiController(inputEl)
const emojiSelectSize = computed(() => {
  if (isMobile.value) {
    return 'medium'
  }

  return variant === 'regular' ? 'large' : 'medium'
})

const gifSelectDisclosure = useDisclosure()
watch(
  () => gifSelectDisclosure.isOpen,
  isOpen => {
    if (isOpen) inputEl.value?.blur()
  }
)

const scheduleDisclosure = useDisclosure()

useSingleDisclosureEnforcing(gifSelectDisclosure, emojiController.emojiSelectDisclosure)

const respondFor = useRespondFor(chatId)
const cancelRespond = useCancelRespond(chatId)

watch(respondFor, respondFor => {
  if (!respondFor) return

  cancelMessageEdit()
  inputEl.value?.focus()
})

const editingMessage = useEditingMessage(chatId)
const cancelMessageEdit = useCancelMessageEdit(chatId)

watch(editingMessage, editingMessage => {
  if (editingMessage) {
    cancelRespond()
    if (editingMessage.attachments.length) attachmentsController.attachmentsModalDisclosure.open()
    inputEl.value?.focus()

    text.value = editingMessage.text ?? ''
    if (editingMessage.attachments) attachments.value = [...editingMessage.attachments]
  } else {
    text.value = ''
    attachmentsController.reset()
  }
})

const sendMessageMutation = useSendMessageMutation(chatId.value)

const editMessageMutation = useEditMessageMutation(chatId.value)

const canSendMessage = computed(() => attachments.value.length > 0 || text.value)
const onMessageSend = (overrides?: Partial<SendMessagePayload>) => {
  if (!canSendMessage.value) return

  const payload = {
    text: text.value,
    attachments: attachments.value,
    respondFor: respondFor.value,
    clientId: crypto.randomUUID(),
    isBBCode: isBBCode.value,
    ...overrides,
  }

  if (editingMessage.value) {
    editMessageMutation.mutate({
      ...payload,
      id: editingMessage.value.id,
    })
  } else {
    sendMessageMutation.mutate(payload)
  }

  text.value = ''
  attachmentsController.reset()
  cancelRespond()
  cancelMessageEdit()
}
const onScheduledSend = (date: Date) => {
  onMessageSend({ scheduledAt: date.toISOString() })
}

useHotkey(inputEl, { code: 'Enter', shift: false }, event => {
  event.preventDefault()
  event.stopPropagation()
  onMessageSend()
})

const onGifMessageSend = (gif: GifObject) =>
  sendMessageMutation.mutate({ attachments: [{ type: 'gif', giphyId: gif.id }], clientId: crypto.randomUUID() })

const onAttachmentsModalClose = () => {
  attachmentsController.reset()
  if (editingMessage) cancelMessageEdit()
}
</script>

<template>
  <div :class="[$style.root, $style['rootVariant' + capitalize(variant)]]">
    <div :class="$style.emojiSelectWrap">
      <SlideTransition :offsetY="10">
        <StaticEmojiSelect
          v-if="emojiController.isEmojiSelectOpen"
          :class="$style.emojiSelect"
          :targetEl="contentEl"
          :size="emojiSelectSize"
          @close="emojiController.onEmojiSelectClose"
          @select="emojiController.onEmojiSelect"
        />
      </SlideTransition>
    </div>

    <SlideTransition :offsetY="10">
      <GifSelect
        v-if="gifSelectDisclosure.isOpen"
        :class="$style.gifSelect"
        :targetEl="contentEl"
        :isOpen="gifSelectDisclosure.isOpen"
        verticalPosition="top-outside"
        horizontalPosition="right-inside"
        :verticalOffset="-10"
        @select="onGifMessageSend"
        @close="gifSelectDisclosure.close"
      />
    </SlideTransition>

    <PingsAutocomplete
      v-if="pingsAutocompleteController.variants.length"
      :class="$style.pingsAutocomplete"
      :prefix="pingsAutocompleteController.pingPrefix"
      :focusedAutocompleteVariantIndex="pingsAutocompleteController.focusedAutocompleteVariantIndex"
      :variants="pingsAutocompleteController.variants"
      @autocomplete-variant-focus="pingsAutocompleteController.onAutocompleteVariantFocus"
      @complete="pingsAutocompleteController.onComplete"
    />

    <MessageEditorRespond v-if="respondFor" :respondFor="respondFor" />

    <MessageEditorEditingMessage v-if="editingMessage" :editingMessage="editingMessage" />

    <div :class="[$style.content, $style['contentVariant' + capitalize(variant)]]" ref="contentEl">
      <div v-if="variant === 'regular'" :class="$style.attachmentWrap">
        <button :class="$style.action" @click="attachmentsController.showFileDialog">
          <IconAttachment />
        </button>
      </div>

      <div :class="$style.inputWrap">
        <textarea
          :class="[$style.input, $style['input' + capitalize(variant)]]"
          :style="{ height: inputHeight + 'px' }"
          :placeholder="placeholder"
          :rows="inputRowCount"
          v-model="text"
          :maxlength="textLimitController.maxTextLength"
          ref="inputEl"
          @focus="notifyScreenKeyboardShown"
          @blur="notifyScreenKeyboardHidden"
        />
      </div>

      <div :class="$style.rightWrap">
        <MessageEditorActions
          :isEmojiSelectOpen="emojiController.isEmojiSelectOpen"
          :isGifSelectOpen="gifSelectDisclosure.isOpen"
          :variant="variant"
          :contentEl="contentEl"
          @emoji-select-toggle="emojiController.onEmojiSelectToggle"
          @gif-select-toggle="gifSelectDisclosure.toggle"
        />
        <div
          v-if="textLimitController.isLengthLimitShown"
          :class="[$style.length, { [$style.lengthWarning]: textLimitController.isLengthLimitWarningShown }]"
        >
          {{ text.length }} / {{ textLimitController.maxTextLength }}
        </div>
      </div>

      <div :class="$style.sendWrap">
        <button
          v-if="isCurrentUserAdmin"
          :class="[$style.action, $style['actionVariant' + capitalize(variant)]]"
          :disabled="!canSendMessage"
          @click="scheduleDisclosure.toggle"
        >
          <IconSchedule />
        </button>

        <button
          :class="[$style.action, $style['actionVariant' + capitalize(variant)]]"
          :disabled="!canSendMessage"
          @click="onMessageSend()"
        >
          <IconSend />
        </button>
      </div>
    </div>

    <MessageEditorBbCodeToggle v-if="isCurrentUserAdmin" :class="$style.bbCodeToggle" v-model="isBBCode" />
  </div>

  <AttachmentsModal
    :attachmentsController="attachmentsController"
    :caption="text"
    @caption-change="text = $event"
    @send="onMessageSend"
    @close="onAttachmentsModalClose"
  />

  <ScheduleModal v-if="isCurrentUserAdmin" :disclosure="scheduleDisclosure" @schedule="onScheduledSend" />
</template>

<style lang="scss" module>
@use '../mixins.scss' as *;
@use '#mixins/scrollbar' as scrollbar;
@use '#mixins/media' as media;

.root {
  @include message-editor-padding;

  & {
    position: relative;
  }

  @include media.mobile {
    @include message-editor-padding-mobile;
  }
}

.rootVariantCompact {
  @include message-editor-padding-comact;
}

.emojiSelectWrap {
  position: relative;
  z-index: 1;
}

.emojiSelect {
  position: absolute;
  bottom: 10px;
  right: 0;
}

.pingsAutocomplete {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  transform: translateY(-100%);
}

.content {
  border: 2px solid #474756;
  border-radius: 4px;
  background-color: #2f303c;
  display: flex;
}

.contentVariantRegular {
  min-height: 48px;

  @include media.mobile {
    min-height: 38px;
  }
}

.contentVariantCompact {
  min-height: 38px;
  height: fit-content;
  box-sizing: border-box;
}

.attachmentWrap {
  border-right: 2px solid #474756;
  display: flex;
  min-width: 0;
  flex-shrink: 0;
  width: auto;
  max-width: 46px;
}

.action {
  @include input-action;

  @include media.mobile {
    @include input-action-compact;
  }
}

.actionVariantCompact {
  @include input-action-compact;
}

.actionActive {
  color: white;
}

.inputWrap {
  flex-grow: 1;
  display: flex;
}

.input {
  outline: none;
  resize: none;
  background: transparent;
  font-family: 'HelveticaNeueSbbChat', 'Noto Color Emoji', system-ui, Avenir, Helvetica, Arial, sans-serif;
  border: none;
  line-height: 1.3;
  min-width: 0;
  margin: 0;
  min-height: fit-content;
  height: 100%;
  width: 0;
  flex-grow: 1;
  box-sizing: content-box;
  color: white !important;

  @include scrollbar.hide;
}

.inputRegular {
  padding: 12px 15px;
  font-size: 16px;
  max-height: 172px;

  @include media.mobile {
    padding: 7px 10px;
    font-size: 14px;
    max-height: 69px;
  }
}

.inputCompact {
  padding: 7px 10px;
  font-size: 14px;
  max-height: 69px;
}

.rightWrap {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-shrink: 0;
}

.length {
  padding: 7px 10px;
  font-size: 13px;
  text-align: right;
  white-space: nowrap;
}

.lengthWarning {
  color: var(--sc-error);
}

.sendWrap {
  border-left: 2px solid #474756;
  flex-shrink: 0;
  display: flex;
  flex-shrink: 0;
}

.bbCodeToggle {
  margin-top: 8px;
}
</style>
